import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ExamSteps from '../create/ExamSteps';
import ExamQuestions from '../create/ExamQuestions';
import { 
  PlusIcon, 
  PhotographIcon, 
  TrashIcon,
  EyeIcon,
  EyeOffIcon,
  QuestionMarkCircleIcon,
  ClockIcon,
  CalendarIcon,
  SaveAsIcon,
  CloudUploadIcon
} from '@heroicons/react/outline';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import { toastService } from '../../../../../services/toast';
import { coursesApi } from '../../../../../services/api/courses.api';

const EditExamPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const examId = id;
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const questionId = queryParams.get('questionId');
  const answerId = queryParams.get('answerId');
  const [currentStep, setCurrentStep] = useState(1);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalAction, setModalAction] = useState(''); // 'publish' or 'draft'
  const [isLoading, setIsLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    totalScore: 0,
    duration: 0,
    retryLimit: false,
    maxAttempts: 3,
    skippable: false,
    isFree: false,
    status: 'draft',
    startDate: format(new Date(), 'yyyy-MM-dd'),
    startTime: '08:00',
    endDate: format(new Date(), 'yyyy-MM-dd'),
    endTime: '10:00',
    autoPublish: false,
    autoUnpublish: false,
    passingScore: 0,
    questions: [],
    instructions: {
      general: '',
      questions: {}
    },
    courseId: '',
    description: ''
  });
  
  // دالة لجلب بيانات الاختبار للتعديل
  const fetchExamData = async () => {
    if (!examId) return;
    try {
      setIsLoading(true);
      const data = await coursesApi.getExamById(examId);
      console.log('البيانات المستلمة من API:', data);
      console.log('قيمة is_free المستلمة:', data.is_free);
      const parseDateTime = (str, part) => {
        if (!str) return part === 'date' ? format(new Date(), 'yyyy-MM-dd') : '08:00';
        const [d, t] = str.split(' ');
        return part === 'date' ? d : (t ? t.substring(0,5) : '08:00');
      };
      setFormData({
        title: data.title || '',
        totalScore: data.total_score ?? 0,
        duration: data.duration ?? 0,
        retryLimit: !!data.retry_limit,
        maxAttempts: data.max_attempts ?? 3,
        skippable: !!data.skippable,
        status: data.status || 'draft',
        startDate: parseDateTime(data.start_date, 'date'),
        startTime: parseDateTime(data.start_date, 'time'),
        endDate: parseDateTime(data.end_date, 'date'),
        endTime: parseDateTime(data.end_date, 'time'),
        autoPublish: false,
        autoUnpublish: false,
        passingScore: data.passing_score ?? 0,
        questions: data.questions || [],
        instructions: data.instructions || { general: '', questions: {} },
        courseId: data.course_id ?? '',
        description: data.description || '',
        isFree: data.is_free || false
      });
    } catch (error) {
      // حدث خطأ أثناء جلب بيانات الاختبار
      toastService.error('حدث خطأ أثناء جلب بيانات الاختبار');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchExamData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examId]);

  useEffect(() => {
    if (!formData.questions) return;

    if (questionId) {
      // Open the question and scroll to it
      const questionIndex = formData.questions.findIndex(q => q.id == questionId);
      if (questionIndex !== -1) {
        // Wait for the next tick to scroll
        setTimeout(() => {
          const element = document.getElementById(`question-${questionId}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 300);
      }
    }

    if (answerId) {
      // Find the question that contains the answer
      const question = formData.questions.find(q => q.answers.some(a => a.id == answerId));
      if (question) {
        // Open the question
        const questionIndex = formData.questions.findIndex(q => q.id == question.id);
        if (questionIndex !== -1) {
          // Wait for the next tick to scroll to the answer
          setTimeout(() => {
            const element = document.getElementById(`answer-${answerId}`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth' });
            }
          }, 300);
        }
      }
    }
  }, [formData.questions, questionId, answerId]);

  // جلب قائمة الدورات عند تحميل الصفحة
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const coursesData = await coursesApi.getCoursesForExam();
        setCourses(coursesData);
      } catch (error) {
        // حدث خطأ أثناء جلب قائمة الدورات
        toastService.error('حدث خطأ أثناء جلب قائمة الدورات');
      }
    };
    
    fetchCourses();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleToggleChange = (name) => {
    setFormData(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  const renderBasicInfo = () => (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
        <div className="p-6">
          <div className="space-y-6">
            {/* الصف الأول: العنوان والدرجة الكلية */}
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  عنوان الاختبار
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="mt-1 block w-full h-10 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="أدخل عنوان الاختبار"
                />
              </div>
              <div>
                <label htmlFor="totalScore" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  الدرجة الكلية
                </label>
                <input
                  type="number"
                  id="totalScore"
                  name="totalScore"
                  value={formData.totalScore}
                  onChange={handleInputChange}
                  className="mt-1 block w-full h-10 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="أدخل الدرجة الكلية"
                />
              </div>
            </div>

            {/* الصف الثاني: مدة الاختبار والدورة */}
            <div className="grid grid-cols-2 gap-6 mt-6">
              <div>
                <label htmlFor="duration" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  مدة الاختبار (بالدقائق)
                </label>
                <input
                  type="number"
                  id="duration"
                  name="duration"
                  value={formData.duration}
                  onChange={handleInputChange}
                  className="mt-1 block w-full h-10 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="أدخل مدة الاختبار"
                />
              </div>
              <div>
                <label htmlFor="courseId" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  الدورة التابع لها
                </label>
                <select
                  id="courseId"
                  name="courseId"
                  value={formData.courseId}
                  onChange={handleInputChange}
                  className="mt-1 block w-full h-10 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  required
                >
                  <option value="">اختر الدورة</option>
                  {courses && courses.length > 0 ? (
                    courses.map(course => (
                      <option key={course.id} value={course.id}>
                        {course.name}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>لا توجد دورات متاحة</option>
                  )}
                </select>
              </div>
            </div>

            <div className="mt-6">
              <label htmlFor="passingScore" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                درجة النجاح (اختياري)
              </label>
              <input
                type="number"
                id="passingScore"
                name="passingScore"
                value={formData.passingScore}
                onChange={handleInputChange}
                className="mt-1 block w-full h-10 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                min="0"
                max={formData.totalScore}
                placeholder="اترك فارغاً إذا لم تكن هناك درجة نجاح محددة"
              />
            </div>

            {/* خيارات الاختبار */}
            <div className="space-y-4 mt-6">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    إعادة الاختبار
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {formData.retryLimit ? 'مسموح بإعادة الاختبار مرة واحدة فقط' : 'مسموح بإعادة الاختبار بلا حدود'}
                  </p>
                </div>
                <div className="flex items-center justify-center w-14">
                  <button
                    type="button"
                    onClick={() => handleToggleChange('retryLimit')}
                    className={`${
                      formData.retryLimit ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                    } flex h-6 w-11 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none`}
                  >
                    <span
                      aria-hidden="true"
                      className={`${
                        formData.retryLimit ? 'mr-5' : 'mr-0'
                      } inline-block h-5 w-5 rounded-full bg-white shadow transition-all duration-200 ease-in-out`}
                    />
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between gap-4">
                <div className="flex-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    تخطي الأسئلة
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {formData.skippable ? 'يمكن للطلاب تخطي الأسئلة والعودة إليها لاحقاً' : 'يجب الإجابة على الأسئلة بالترتيب'}
                  </p>
                </div>
                <div className="flex items-center justify-center w-14">
                  <button
                    type="button"
                    onClick={() => handleToggleChange('skippable')}
                    className={`${
                      formData.skippable ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                    } flex h-6 w-11 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none`}
                  >
                    <span
                      aria-hidden="true"
                      className={`${
                        formData.skippable ? 'mr-5' : 'mr-0'
                      } inline-block h-5 w-5 rounded-full bg-white shadow transition-all duration-200 ease-in-out`}
                    />
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between gap-4 mt-4">
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  منشور
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {formData.status === 'published' ? 'الاختبار منشور ومتاح للطلاب' : 'الاختبار مخفي ولا يمكن للطلاب رؤيته'}
                </p>
              </div>
              <div className="flex items-center justify-center w-14">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    status: prev.status === 'published' ? 'draft' : 'published'
                  }))}
                  className={`${
                    formData.status === 'published' ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                  } flex h-6 w-11 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none`}
                >
                  <span
                    aria-hidden="true"
                    className={`${
                      formData.status === 'published' ? 'mr-5' : 'mr-0'
                    } inline-block h-5 w-5 rounded-full bg-white shadow transition-all duration-200 ease-in-out`}
                  />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between gap-4 mt-4">
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  مجاني
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {formData.isFree ? 'الاختبار متاح للجميع مجاناً' : 'الاختبار مدفوع ويتطلب شراء الدورة'}
                </p>
              </div>
              <div className="flex items-center justify-center w-14">
                <button
                  type="button"
                  onClick={() => handleToggleChange('isFree')}
                  className={`${
                    formData.isFree ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                  } flex h-6 w-11 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none`}
                >
                  <span
                    aria-hidden="true"
                    className={`${
                      formData.isFree ? 'mr-5' : 'mr-0'
                    } inline-block h-5 w-5 rounded-full bg-white shadow transition-all duration-200 ease-in-out`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // تم إزالة الإعدادات المتقدمة

  const handleSave = async (navigateAfterSave = true) => {
    try {
      setIsLoading(true);
      
      // التحقق من البيانات المطلوبة
      if (!formData.title) {
        toastService.error('يجب إدخال عنوان الاختبار');
        setIsLoading(false);
        return;
      }
      
      // التحقق من اختيار الدورة (إجباري)
      if (!formData.courseId) {
        toastService.error('يجب اختيار الدورة التابع لها الاختبار');
        setIsLoading(false);
        return;
      }
      
      // استخدام الحالة من السويتش الذي اختاره المستخدم
      const updatedFormData = {
        ...formData
      };
      setFormData(updatedFormData);
      
      // تحضير البيانات للإرسال
      const examData = {
        title: updatedFormData.title,
        description: updatedFormData.description || '',
        courseId: updatedFormData.courseId,
        duration: updatedFormData.duration,
        totalScore: updatedFormData.totalScore,
        passingScore: updatedFormData.passingScore,
        status: updatedFormData.status,
        retryLimit: updatedFormData.retryLimit,
        maxAttempts: updatedFormData.maxAttempts,
        skippable: updatedFormData.skippable,
        is_free: updatedFormData.isFree || false
      };
      
      // إضافة التواريخ إذا كانت متوفرة
      if (updatedFormData.startDate && updatedFormData.startTime) {
        examData.startDate = `${updatedFormData.startDate} ${updatedFormData.startTime}`;
      }
      
      if (updatedFormData.endDate && updatedFormData.endTime) {
        examData.endDate = `${updatedFormData.endDate} ${updatedFormData.endTime}`;
      }
      
      // إرسال البيانات إلى API
      const response = await coursesApi.updateExam(examId, examData);
      // تم تحديث الاختبار بنجاح
      
      toastService.success('تم تحديث الاختبار بنجاح');
      
      if (navigateAfterSave) {
        // الانتقال إلى صفحة الاختبارات بعد الحفظ
        setTimeout(() => {
          navigate('/admin/dashboard/courses/exams');
        }, 1500);
      }
      
      return true; // نجاح
    } catch (error) {
      // حدث خطأ أثناء حفظ الاختبار
      if (error.response && error.response.status === 422) {
        const errors = error.response.data.errors;
        const errorMessage = Object.values(errors).flat()[0] || 'حدث خطأ أثناء حفظ الاختبار';
        toastService.error(errorMessage);
      } else {
        toastService.error('حدث خطأ أثناء حفظ الاختبار');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveAndNext = async () => {
    const success = await handleSave(false); // لا ننتقل بعد الحفظ
    if (success) {
      setCurrentStep(prev => prev + 1);
    }
  };

  // ==================== دوال إدارة الأسئلة ====================
  // إضافة سؤال جديد
  const handleAddQuestion = async (newQuestion) => {
    // إضافة مؤقتة مع حالة تحميل
    const tempId = Date.now();
    const order = formData.questions.length + 1;
    const tempQuestion = { ...newQuestion, tempId, order, isSaving: true };

    setFormData(prev => ({
      ...prev,
      questions: [...prev.questions, tempQuestion]
    }));

    if (!examId) return; // لا يمكن الحفظ بدون معرف الاختبار

    try {
      const response = await coursesApi.addExamQuestion(examId, { ...newQuestion, order });
      if (response) {
        // بعد نجاح الإضافة نجلب القائمة من الخادم
        try {
          const serverQuestions = await coursesApi.getExamQuestions(examId);
          if (Array.isArray(serverQuestions)) {
            setFormData(prev => ({
              ...prev,
              questions: serverQuestions.sort((a, b) => (a.order || 0) - (b.order || 0))
            }));
          }
          return;
        } catch (fetchErr) {
          // خطأ أثناء جلب الأسئلة بعد الإضافة
        }
      }
    } catch (error) {
      // خطأ أثناء حفظ السؤال
      toastService.error('حدث خطأ أثناء حفظ السؤال');
      // إزالة السؤال المؤقت
      setFormData(prev => ({
        ...prev,
        questions: prev.questions.filter(q => q.tempId !== tempId)
      }));
    }
  };

  // حفظ مباشر من مكوّن الطفل إن لزم
  const handleDirectSaveQuestion = async (examId, questionData) => {
    return await coursesApi.addExamQuestion(examId, questionData);
  };

  // حذف سؤال
  const handleDeleteQuestion = async (index, questionId) => {
    try {
      // حذف السؤال

      if (!examId) {
        // لا يوجد معرف اختبار => حذف محلي فقط
        setFormData(prev => ({
          ...prev,
          questions: prev.questions.filter((_, i) => i !== index)
        }));
        toastService.warning('تم حذف السؤال من القائمة فقط لأن معرف الاختبار غير متوفر');
        return;
      }

      if (index < 0 || index >= formData.questions.length) {
        toastService.error('مؤشر السؤال غير صالح');
        return;
      }

      const questionToDelete = formData.questions[index];
      if (questionToDelete.tempId || !questionToDelete.id) {
        // سؤال غير محفوظ بعد => حذف محلي فقط
        setFormData(prev => ({
          ...prev,
          questions: prev.questions.filter((_, i) => i !== index)
        }));
        return;
      }

      // استخراج معرف السؤال الحقيقي
      const actualQuestionId = questionToDelete.id || questionToDelete.db_id || questionToDelete._id || questionToDelete.question_id || questionId;

      if (!actualQuestionId) {
        toastService.warning('لم يتم العثور على معرف السؤال');
        return;
      }

      // محاولة الحذف النهائي، وإن فشل استخدم الحذف الناعم
      try {
        await coursesApi.permanentDeleteExamQuestion(examId, actualQuestionId);
        toastService.success('تم حذف السؤال وإجاباته نهائياً بنجاح');
      } catch (apiErr) {
        // فشل الحذف النهائي، استخدام الحذف الناعم كاحتياطي
        await coursesApi.deleteExamQuestion(examId, actualQuestionId);
        toastService.warning('تم حذف السؤال بشكل ناعم فقط');
      }

      // تحديث القائمة محلياً أولاً
      const updatedQuestionsLocal = formData.questions.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, questions: updatedQuestionsLocal }));

      // ثم جلب القائمة من السيرفر للتأكيد
      try {
        const serverQuestions = await coursesApi.getExamQuestions(examId);
        if (Array.isArray(serverQuestions)) {
          // دمج الأسئلة المحلية غير المحفوظة (temp)
          const remainingTemps = updatedQuestionsLocal.filter(q => q.tempId);
          const merged = [...serverQuestions, ...remainingTemps].sort((a, b) => (a.order || 0) - (b.order || 0));
          setFormData(prev => ({ ...prev, questions: merged }));
        }
      } catch (refreshErr) {
        // حدث خطأ أثناء تحديث قائمة الأسئلة
        toastService.warning('تم الحذف ولكن فشل تحديث الأسئلة تلقائياً');
      }
    } catch (err) {
      // حدث خطأ أثناء محاولة حذف السؤال
      toastService.error('حدث خطأ أثناء محاولة حذف السؤال');
    }
  };

  // تحديث سؤال (قابل للتنفيذ لاحقاً)
  const handleUpdateQuestion = () => {};

  return (
    <div className="p-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
        {/* رأس البطاقة */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h1 className="text-lg font-medium text-gray-900 dark:text-white">تعديل الاختبار</h1>
        </div>

        {/* Stepper */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <ExamSteps currentStep={currentStep} />
        </div>

        {/* محتوى البطاقة */}
        <div className="p-6">
          {currentStep === 1 && renderBasicInfo()}
          {currentStep === 2 && (
            <ExamQuestions
              questions={formData.questions}
              onAddQuestion={handleAddQuestion}
              onUpdateQuestion={handleUpdateQuestion}
              onDeleteQuestion={handleDeleteQuestion}
              examId={examId}
              onDirectSave={handleDirectSaveQuestion}
              totalScore={formData.totalScore}
            />
          )}
        </div>

        {/* أزرار التنقل */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 rounded-b-lg border-t border-gray-200 dark:border-gray-600 flex justify-between items-center">
          <div>
            {currentStep > 1 && (
              <button
                type="button"
                onClick={() => setCurrentStep(prev => prev - 1)}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors duration-200 min-w-[120px]"
              >
                السابق
              </button>
            )}
          </div>
          
          <div className="flex gap-4">
            <button
              type="button"
              onClick={currentStep < 2 ? handleSaveAndNext : handleSave}
              className="px-4 py-2.5 text-sm font-medium text-white rounded-md transition-colors duration-200 min-w-[120px] bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
            >
              {currentStep < 2 ? 'حفظ والتالي' : 'حفظ'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditExamPage;
